import { useState, useEffect } from 'react';

interface UseResponsiveOptions {
  mobileBreakpoint?: number;
  onMobileChange?: (isMobile: boolean, wasMobile: boolean) => void;
}

interface UseResponsiveReturn {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
}

export const useResponsive = (options: UseResponsiveOptions = {}): UseResponsiveReturn => {
  const { mobileBreakpoint = 768, onMobileChange } = options;
  const tabletBreakpoint = 1024;

  const [screenWidth, setScreenWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 1024; // Default for SSR
  });

  const [isMobile, setIsMobile] = useState(() => screenWidth < mobileBreakpoint);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newIsMobile = newWidth < mobileBreakpoint;
      const wasMobile = isMobile;

      setScreenWidth(newWidth);
      
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        onMobileChange?.(newIsMobile, wasMobile);
      }
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint, isMobile, onMobileChange]);

  return {
    isMobile,
    isTablet: screenWidth >= mobileBreakpoint && screenWidth < tabletBreakpoint,
    isDesktop: screenWidth >= tabletBreakpoint,
    screenWidth,
  };
};
